import React, { useState, useRef, useEffect, useCallback } from 'react';
import { VscAttach } from 'react-icons/vsc';
import { BsFileEarmark } from 'react-icons/bs';
import { uploadChatFile } from '../../api/chat';

function ChatInput({ onSend, allUsers, socket, groupId }) {
  const [text, setText]               = useState('');
  const [pendingFiles, setPending]    = useState([]); // { id, name, localUrl, uploadedUrl, mimetype, uploading, isImage }
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionSel, setMentionSel]     = useState(0);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastTypingSent = useRef(0);

  const mentionOptions = mentionQuery
    ? [{ _id: '__all', username: 'all', role: 'all' },
       ...allUsers.filter(u => u.username.toLowerCase().startsWith(mentionQuery.query))]
    : [];

  const emitTyping = useCallback(() => {
    if (!socket || !groupId) return;
    const now = Date.now();
    if (now - lastTypingSent.current > 2000) {
      lastTypingSent.current = now;
      socket.emit('typing:start', { groupId });
    }
  }, [socket, groupId]);

  const uploadFile = async (file) => {
    const isImage  = file.type.startsWith('image/');
    const localUrl = isImage ? URL.createObjectURL(file) : null;
    const id       = Date.now() + Math.random();
    setPending(prev => [...prev, { id, name: file.name, localUrl, uploadedUrl: null, mimetype: file.type, uploading: true, isImage }]);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await uploadChatFile(form);
      setPending(prev => prev.map(p => p.id === id
        ? { ...p, uploadedUrl: res.data.url, name: res.data.name || file.name, mimetype: res.data.mimetype || file.type, uploading: false }
        : p
      ));
    } catch {
      setPending(prev => prev.filter(p => p.id !== id));
      if (localUrl) URL.revokeObjectURL(localUrl);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.kind === 'file') {
        e.preventDefault();
        uploadFile(item.getAsFile());
        return;
      }
    }
  };

  const handleFileChange = (e) => {
    Array.from(e.target.files || []).forEach(uploadFile);
    e.target.value = '';
  };

  const removeFile = (id) => {
    setPending(prev => {
      const f = prev.find(p => p.id === id);
      if (f?.localUrl) URL.revokeObjectURL(f.localUrl);
      return prev.filter(p => p.id !== id);
    });
  };

  const insertMention = (username) => {
    if (mentionQuery === null) return;
    const before  = text.slice(0, mentionQuery.startIdx);
    const after   = text.slice(mentionQuery.startIdx + 1 + mentionQuery.query.length);
    setText(before + '@' + username + ' ' + after);
    setMentionQuery(null);
    textareaRef.current?.focus();
  };

  const handleChange = (e) => {
    const val    = e.target.value;
    setText(val);
    emitTyping();
    const cursor = e.target.selectionStart;
    const match  = val.slice(0, cursor).match(/@(\w*)$/);
    if (match) {
      setMentionQuery({ query: match[1].toLowerCase(), startIdx: cursor - 1 - match[1].length });
      setMentionSel(0);
    } else {
      setMentionQuery(null);
    }
  };

  const handleKeyDown = (e) => {
    if (mentionQuery && mentionOptions.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionSel(s => Math.min(s + 1, mentionOptions.length - 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setMentionSel(s => Math.max(s - 1, 0)); return; }
      if (e.key === 'Tab' || e.key === 'Enter') {
        if (mentionOptions[mentionSel]) { e.preventDefault(); insertMention(mentionOptions[mentionSel].username); return; }
      }
      if (e.key === 'Escape') { setMentionQuery(null); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    const trimmed     = text.trim();
    const anyUploading = pendingFiles.some(p => p.uploading);
    if (!trimmed && !pendingFiles.length) return;
    if (anyUploading) return;
    const attachments = pendingFiles.map(p => ({ url: p.uploadedUrl, name: p.name, mimetype: p.mimetype }));
    onSend(trimmed, attachments);
    setText('');
    setPending(prev => { prev.forEach(p => { if (p.localUrl) URL.revokeObjectURL(p.localUrl); }); return []; });
    setMentionQuery(null);
  };

  const anyUploading = pendingFiles.some(p => p.uploading);
  const canSend = (text.trim() || pendingFiles.length > 0) && !anyUploading;

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
  }, [text]);

  return (
    <div className="chat-input-area">
      {mentionQuery && mentionOptions.length > 0 && (
        <div className="chat-mention-dropdown">
          {mentionOptions.map((u, i) => (
            <div key={u._id}
              className={`chat-mention-option${i === mentionSel ? ' selected' : ''}`}
              onMouseDown={e => { e.preventDefault(); insertMention(u.username); }}>
              <span className={`role-dot role-dot-${u.role}`} />
              <span className={`chat-sender role-${u.role}`}>
                {u.displayName || u.username}
                {u.displayName && <span style={{ color: 'var(--text-muted)', marginLeft: 5, fontSize: 11 }}>@{u.username}</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      {pendingFiles.length > 0 && (
        <div className="chat-pending-files">
          {pendingFiles.map(f => f.isImage ? (
            <div key={f.id} className="chat-img-preview">
              <img src={f.localUrl} className={f.uploading ? 'uploading' : ''} alt="" />
              {!f.uploading && <button className="chat-img-remove" onClick={() => removeFile(f.id)}>✕</button>}
            </div>
          ) : (
            <div key={f.id} className={`chat-file-pending${f.uploading ? ' uploading' : ''}`}>
              <BsFileEarmark size={14} />
              <span className="chat-file-pending-name">{f.name}</span>
              {!f.uploading && <button className="chat-img-remove" onClick={() => removeFile(f.id)}>✕</button>}
            </div>
          ))}
        </div>
      )}

      <div className="chat-input-row">
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFileChange} />
        <button className="chat-attach-btn" title="Attach file" onClick={() => fileInputRef.current?.click()} type="button">
          <VscAttach size={16} />
        </button>
        <textarea
          ref={textareaRef}
          className="chat-input"
          placeholder={window.innerWidth <= 768 ? 'Message…' : 'Message… (Enter to send, Shift+Enter for new line)'}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          rows={1}
        />
        <button className="chat-send-btn" onClick={handleSend} disabled={!canSend}>Send</button>
      </div>
    </div>
  );
}

export default ChatInput;
